import ApiService from "@/core/services/ApiService";
import JwtService from "@/core/services/JwtService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";
import { ICurrentUser } from "@/interfaces/iCurrentUser";
import { AxiosRequestConfig } from "axios";
import { useToast } from "vue-toastification";
import { UserAPI } from "@/services/api/User.service";

export interface UserAuthInfo {
  errors: unknown;
  access_token: string | null;
  user: ICurrentUser;
  isAuthenticated: boolean;
  clientDetails: any;
}

@Module
export default class AuthModule extends VuexModule implements UserAuthInfo {
  errors = {};
  access_token = JwtService.getToken();
  user = {} as ICurrentUser;
  isAuthenticated = !!JwtService.getToken();
  clientDetails: any = {};

  /**
   * Get current user object
   * @returns User
   */
  get currentUser(): ICurrentUser {
    return this.user;
  }

  /**
   * Verify user authentication
   * @returns boolean
   */
  get isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Get authentification errors
   * @returns array
   */
  get getErrors() {
    return this.errors;
  }

  /**
   * Get client details
   */
  get getClientDetails() {
    return this.clientDetails;
  }

  @Mutation
  [Mutations.SET_ERROR](error) {
    this.errors = error;
  }

  @Mutation
  [Mutations.SET_AUTH](access_token: string) {
    this.isAuthenticated = true;
    this.access_token = access_token;
    this.errors = [];
    JwtService.saveToken(this.access_token);
  }

  @Mutation
  [Mutations.SET_USER](user: ICurrentUser) {
    this.user = user;
    JwtService.saveCurrentUser(user);
  }

  @Mutation
  [Mutations.SET_PASSWORD](password: string) {
    this.user.password = password;
  }

  @Mutation
  [Mutations.PURGE_AUTH]() {
    this.isAuthenticated = false;
    this.user = {} as ICurrentUser;
    this.clientDetails = {};
    this.errors = [];
    JwtService.destroyToken();
    JwtService.removeCurrentUser();
  }

  @Mutation
  [Mutations.SET_CLIENT_DETAILS](details: any) {
    this.clientDetails = details;
  }

  @Action
  [Actions.LOGIN]({
    username,
    password,
  }: {
    username: string;
    password: string;
  }) {
    return new Promise<void>((resolve, reject) => {
      ApiService.post(
        "oauth/token",
        `username=${encodeURIComponent(username)}&password=${encodeURIComponent(
          password
        )}&grant_type=password`,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: "Basic U1NBcHA6cGFzc3dvcmQ=",
          },
        }
      )
        .then(async ({ data }) => {
          this.context.commit(Mutations.SET_AUTH, data.access_token);
          await this.context.dispatch("updateUser");
          await this.context.dispatch(Actions.GET_CLIENT_DETAILS); // Load client details
          resolve();
        })
        .catch(({ response }) => {
          const toast = useToast();
          toast.error(response.data.error_description);
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
          reject(response);
        });
    });
  }

  @Action
  [Actions.LOGOUT]() {
    this.context.commit(Mutations.PURGE_AUTH);
    this.context.dispatch("SITE_STORE/RESET_USER_SELECTED_SITES", null, {
      root: true,
    });
  }

  @Action
  [Actions.REGISTER](credentials) {
    return ApiService.post("register", credentials)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_AUTH, data);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, response.data.errors);
      });
  }

  @Action({ rawError: true })
  [Actions.FORGOT_PASSWORD](payload) {
    return ApiService.post(`forgetPassword?email=${payload.email}`, {})
      .then(({ data }) => {
        const toast = useToast();
        toast.success("Password reset email has been sent successfully!");
        this.context.commit(Mutations.SET_AUTH, data);
      })
      .catch(({ response }) => {
        const toast = useToast();
        toast.error(response.data.message);
        this.context.commit(Mutations.SET_ERROR, response.data.errors);
      });
  }

  @Action({ rawError: true })
  async [Actions.VERIFY_AUTH]() {
    if (!this.access_token) {
      this.context.commit(Mutations.PURGE_AUTH);
    }
    if (!this.user.webId) {
      try {
        await this.context.dispatch("updateUser");
        await this.context.dispatch(Actions.GET_CLIENT_DETAILS); // Refresh client details after reload
      } catch (e) {
        this.context.commit(Mutations.PURGE_AUTH);
      }
    }
  }

  @Action({ rawError: true })
  [Actions.UPDATE_USER]() {
    const requestConfig: AxiosRequestConfig = {
      headers: {
        Authorization: `bearer ${JwtService.getToken()}`,
      },
    };
    return new Promise<void>((resolve, reject) => {
      if (!JwtService.getToken()) {
        reject("No token found");
      }
      ApiService.get("security/userAccounts/currentUser", "", {
        headers: requestConfig.headers,
      })
        .then(({ data }) => {
          this.context.commit(Mutations.SET_USER, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
          reject(response.data.errors);
        });
    });
  }

  @Action({ rawError: true })
  async [Actions.GET_CLIENT_DETAILS]() {
    if (!this.user?.clientId) return;
    try {
      const details = await UserAPI.getInstance().getClientDetails(
        this.user.clientId
      );
      this.context.commit(Mutations.SET_CLIENT_DETAILS, details);
    } catch (error: any) {
      const toast = useToast();
      toast.error("Failed to load client details");
      this.context.commit(Mutations.SET_ERROR, error.response?.data || error);
    }
  }
}
